import * as Sentry from '@sentry/react';
import { AppName } from 'sl-api-connector';
import ReduxStore from 'src/types/store/reduxStore';
import { getAppName, isDrive, shouldUseHighSentryReplaySampleRate } from './app';

let isInitialized = false;
const appName = getAppName();

export function initializeSentry(app: ReduxStore['app']) {
  const {
    config: { atlasSentryApiKey, omniSentryApiKey, beaconSentryApiKey, adManagerSentryApiKey }
  } = app;
  let token: string | null = null;

  if (appName === AppName.Advertising) {
    token = adManagerSentryApiKey;
  } else if (appName === AppName.Atlas) {
    token = atlasSentryApiKey;
  } else if (appName === AppName.Beacon) {
    token = beaconSentryApiKey;
  } else if (appName === AppName.Omni) {
    token = omniSentryApiKey;
  }

  const release = `${process.env.COMMIT_SHA}${appName}${app.stage === 'dev' ? '-dev' : ''}`;

  // Use Split IO to determine if high sample rate should be used
  const useHighSampleRate = shouldUseHighSentryReplaySampleRate();

  if (token) {
    Sentry.init({
      dsn: token,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false
        })
      ],
      release,
      // We recommend adjusting this value in production, or using tracesSampler
      // for finer control
      tracesSampleRate: isDrive ? 1 : 0.2, // sample all transactions for drive
      // Session Replay - now controlled by Split IO
      replaysSessionSampleRate: useHighSampleRate ? 1.0 : 0.2, // Split IO controls sample rate
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
      beforeSend(event) {
        // Check if the event has an HTTP status code
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);

        // Convert query parameters to an object
        const queryParams = {};
        urlParams.forEach((value, key) => {
          queryParams[key] = value;
        });
        // Setting tags for sentry
        event.tags = { ...event.tags, ...queryParams };
        return event;
      }
    });
    isInitialized = true;
  }
}

export function isSentryInitialized() {
  return isInitialized;
}

export function setSentryUserIdentity(
  session: { clientIP?: string; trackingId: string },
  userConfig: ReduxStore['user']['config']
) {
  const { firstName, lastName, company, email } = userConfig.profile;
  const { clientIP } = session;
  Sentry.setUser({ email, firstName, lastName, company, ip_address: clientIP });
}
